import type { CloudAllowanceStatus, CloudWorkspace } from "@afar-research/contracts";

import {
  type CloudUsageBlocker,
  cloudUsageBlocker,
} from "../../components/settings/CloudWorkspaces.logic";

/** What the Runtime Web Client learned about a signed-in account before opening the app. */
export type AccountRead =
  | { readonly _tag: "failed" }
  | {
      readonly _tag: "read";
      readonly allowance: CloudAllowanceStatus;
      readonly workspaces: ReadonlyArray<Pick<CloudWorkspace, "id" | "state">>;
      /** The workspaces this client uses; undefined before it has used any. */
      readonly added: ReadonlyArray<string> | undefined;
      /** The Environments this client reached through the account's AFAR Network. */
      readonly networkMachines: ReadonlyArray<string>;
      /** This session chose to add its own machine rather than create a Cloud Workspace. */
      readonly addingOwnMachine?: boolean;
      /** This page was opened from an Organization invitation link, which the open app accepts. */
      readonly openingInvitation?: boolean;
    };

export type AccountEntry =
  | { readonly kind: "app" }
  | { readonly kind: "unreadable" }
  | { readonly kind: "plan-gate"; readonly reason: CloudUsageBlocker }
  | { readonly kind: "first-workspace-setup" };

/**
 * The Runtime Web Client's step after sign-in: the Plan Gate for an account that cannot use AFAR
 * Cloud, First Workspace Setup for a client that uses none of the account's Cloud Workspaces and
 * none of its own machines, else the app. A client that has never used one is shown the account's
 * workspaces rather than adopting them unasked. A machine reached through the account's AFAR Network
 * is as good a way in as a workspace (ADR-0146), and so is an Organization invitation link, which
 * is accepted in Settings.
 * Administrators skip both steps, so one without a plan can still attach one. An unread account
 * never opens the app, which would have nothing to show.
 */
export function resolveAccountEntry(read: AccountRead): AccountEntry {
  if (read._tag === "failed") return { kind: "unreadable" };
  if (read.allowance.admin) return { kind: "app" };
  const blocker = cloudUsageBlocker(read.allowance);
  if (blocker !== null) return { kind: "plan-gate", reason: blocker };
  const added = read.added ?? [];
  const connectable =
    read.workspaces.some(
      (workspace) =>
        workspace.state !== "deleting" &&
        workspace.state !== "deleted" &&
        added.includes(workspace.id),
    ) ||
    read.networkMachines.some((id) => added.includes(id)) ||
    read.addingOwnMachine === true ||
    read.openingInvitation === true;
  return connectable ? { kind: "app" } : { kind: "first-workspace-setup" };
}
